import { useActualIncidentId } from './commonHooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';
import { useAppSelector, selectUserGuid } from 'slices/commonSelectors';

type Agenda = {
  [phaseIndex: number]: { notes?: string[]; checked?: string[] };
};

export type IncidentInfo = {
  id: UUID;
  incidentId: UUID;
  version: number;
  timestamp: Date;
  userGuid: UUID;
  situationSummary: string;
  priorities: string;
  keyDecisionsAndProcedures: string;
  limitationsAndConstraints: string;
  criticalInformationRequirements: string;
  communicationsSpecialInstructions: string;
  mapSketchUrl: string;
  safetyPlanUrl: string;
  operationalPeriodCommandEmphasis: string;
  specialMedicalEmergencyProcedures: string;
  medicalAirAssetsYn: boolean;
  incidentLocation: string;
  communityLifelines?: string[];
  siteSafetyPlan?: boolean;
  siteSafetyPlanLocation?: string;
  agendaItems?: Agenda;
  nextOperationalPeriodStartDateTime?: Date | null;
  nextOperationalPeriodEndDateTime?: Date | null;
};

export const keys = {
  base: 'incident_info',
  get all() {
    return [keys.base];
  },
  get: (incidentId: UUID) => [keys.base, incidentId],
};

export const useIncidentInfo = (enabled: boolean = true) => {
  const incidentId = useActualIncidentId();
  return useQuery({
    queryKey: keys.get(incidentId),
    queryFn: async () => {
      const resp = await axios.get<IncidentInfo>(
        `${config.apiGateway.ics_incident_info}/${incidentId}`
      );
      return resp.data;
    },
    enabled,
  });
};

type EditIncidentInfoReqData = Partial<
  Omit<
    IncidentInfo,
    'id' | 'incidentId' | 'userGuid' | 'version' | 'timestamp'
  >
>;

export const useUpdateIcsInfo = () => {
  const incidentId = useActualIncidentId();
  const userGuid = useAppSelector(selectUserGuid);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: EditIncidentInfoReqData) => {
      return await axios.put(
        `${config.apiGateway.ics_incident_info}/${incidentId}`,
        { ...data, userGuid }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: keys.get(incidentId),
      });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};
